import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface DocHubTag {
  id: string;
  brand_id: string;
  name: string;
  color: string;
  description?: string;
  parent_id?: string | null;
  level: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
  children?: DocHubTag[];
}

export const useDocHubTags = (brandId: string) => {
  const queryClient = useQueryClient();

  const {
    data: tags,
    isLoading,
    error
  } = useQuery({
    queryKey: ['doc-hub-tags', brandId],
    queryFn: async (): Promise<DocHubTag[]> => {
      const { data, error } = await supabase
        .from('os_doc_hub_files_tags')
        .select('*')
        .eq('brand_id', brandId)
        .order('name');

      if (error) throw error;
      return data || [];
    },
    enabled: !!brandId,
  });

  const createTagMutation = useMutation({
    mutationFn: async (tagData: Omit<DocHubTag, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('os_doc_hub_files_tags')
        .insert(tagData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doc-hub-tags', brandId] });
      toast({
        title: "Success",
        description: "Tag created successfully",
      });
    },
    onError: (error) => {
      console.error('Error creating tag:', error);
      toast({
        title: "Error",
        description: "Failed to create tag",
        variant: "destructive",
      });
    },
  });

  const updateTagMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<DocHubTag> & { id: string }) => {
      const { data, error } = await supabase
        .from('os_doc_hub_files_tags')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doc-hub-tags', brandId] });
      toast({
        title: "Success",
        description: "Tag updated successfully",
      });
    },
    onError: (error) => {
      console.error('Error updating tag:', error);
      toast({
        title: "Error",
        description: "Failed to update tag",
        variant: "destructive",
      });
    },
  });

  const deleteTagMutation = useMutation({
    mutationFn: async (tagId: string) => {
      // First, get all documents that have this tag
      const { data: documents, error: fetchError } = await supabase
        .from('os_doc_hub_files')
        .select('id, tags')
        .eq('brand_id', brandId);

      if (fetchError) throw fetchError;

      // Update each document to remove the tag
      for (const document of documents || []) {
        let currentTags: string[] = [];
        
        // Parse existing tags
        if (Array.isArray(document.tags)) {
          currentTags = (document.tags as any[]).map(String);
        } else if (typeof document.tags === 'string') {
          try {
            const parsed = JSON.parse(document.tags);
            currentTags = Array.isArray(parsed) ? parsed.map(String) : [];
          } catch {
            currentTags = [];
          }
        }

        // Remove the tag if it exists
        if (currentTags.includes(tagId)) {
          const updatedTags = currentTags.filter(id => id !== tagId);
          
          const { error: updateError } = await supabase
            .from('os_doc_hub_files')
            .update({ tags: JSON.stringify(updatedTags) })
            .eq('id', document.id);

          if (updateError) throw updateError;
        }
      }

      // Then delete the tag
      const { error } = await supabase
        .from('os_doc_hub_files_tags')
        .delete()
        .eq('id', tagId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doc-hub-tags', brandId] });
      queryClient.invalidateQueries({ queryKey: ['os-doc-hub-files', brandId] });
      toast({
        title: "Success",
        description: "Tag deleted successfully",
      });
    },
    onError: (error) => {
      console.error('Error deleting tag:', error);
      toast({
        title: "Error",
        description: "Failed to delete tag",
        variant: "destructive",
      });
    },
  });

  const buildTagTree = (flatTags: DocHubTag[]): DocHubTag[] => {
    const tagMap = new Map<string, DocHubTag>();
    const rootTags: DocHubTag[] = [];

    // First pass: create map of all tags with children array
    flatTags.forEach(tag => {
      tagMap.set(tag.id, { ...tag, children: [] });
    });

    // Second pass: build tree structure
    flatTags.forEach(tag => {
      const tagWithChildren = tagMap.get(tag.id)!;
      if (tag.parent_id) {
        const parent = tagMap.get(tag.parent_id);
        if (parent) {
          parent.children!.push(tagWithChildren);
        } else {
          // Orphaned tag, add to root
          rootTags.push(tagWithChildren);
        }
      } else {
        rootTags.push(tagWithChildren);
      }
    });

    // Sort by sort_order at each level
    const sortTags = (tags: DocHubTag[]) => {
      tags.sort((a, b) => a.sort_order - b.sort_order);
      tags.forEach(tag => {
        if (tag.children && tag.children.length > 0) {
          sortTags(tag.children);
        }
      });
    };
    sortTags(rootTags);

    return rootTags;
  };

  const createTag = (tagData: Omit<DocHubTag, 'id' | 'created_at' | 'updated_at'>) => {
    createTagMutation.mutate(tagData);
  };

  const updateTag = (id: string, updates: Partial<DocHubTag>) => {
    updateTagMutation.mutate({ id, ...updates });
  };

  const deleteTag = (id: string) => {
    deleteTagMutation.mutate(id);
  };

  return {
    tags: tags || [],
    tagTree: buildTagTree(tags || []),
    isLoading,
    error,
    createTag,
    updateTag,
    deleteTag,
    isCreating: createTagMutation.isPending,
    isUpdating: updateTagMutation.isPending,
    isDeleting: deleteTagMutation.isPending,
  };
};
